/**
 * SmartProse.tsx - Intelligent Prose Component with Auto Font Detection
 * 
 * Wraps the Prose component and automatically detects Arabic/Kurdish text
 * to apply the appropriate font (Muna for Arabic/Kurdish, KV-Text for others).
 */

"use client"

import { Prose } from "./prose"
import { htmlContainsArabicOrKurdish } from "@/utilities/detectScript"
import { useMemo, ReactNode, HTMLAttributes } from "react"

interface SmartProseProps extends Omit<HTMLAttributes<HTMLDivElement>, 'dir'> {
  children: ReactNode
  htmlContent?: string
  dir?: "auto" | "ltr" | "rtl"
  maxWidth?: string
  className?: string
}

/**
 * SmartProse - Automatically applies Arabic/Kurdish font when detected
 * 
 * Usage:
 *   <SmartProse htmlContent={post.content.nsHtml} dir="auto" maxWidth="unset">
 *     <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
 *   </SmartProse>
 * 
 * Or with children:
 *   <SmartProse dir="auto">
 *     {content}
 *   </SmartProse>
 */
export const SmartProse: React.FC<SmartProseProps> = ({ 
  children, 
  htmlContent, 
  dir = "auto",
  maxWidth,
  className,
  ...props 
}) => {
  // Detect Arabic/Kurdish in HTML content if provided
  // Note: Detection only works with htmlContent prop. For plain children,
  // use SmartText component instead.
  const hasArabicOrKurdish = useMemo(() => {
    if (htmlContent) {
      return htmlContainsArabicOrKurdish(htmlContent)
    }
    return false
  }, [htmlContent])

  const fontClassName = hasArabicOrKurdish ? 'arabic-kurdish-font' : undefined
  const combinedClassName = [className, fontClassName].filter(Boolean).join(' ') || undefined

  return (
    <Prose 
      dir={dir} 
      maxWidth={maxWidth}
      className={combinedClassName}
      {...props}
    >
      {children}
    </Prose>
  )
}

